<template>
  <div v-if="currentExportList" class="view-gabarit">
    <h2 class="head-box-title">{{ $t('exportStudy') }}</h2>

    <div class="last-export-box">
      <h3>{{ $t('modal.lastExportInProgress') }}</h3>

      <div class="export-content">
        <div v-if="lastExport" class="last-box">
          <div class="icon-container">
            <i class="icon-rapport" />
          </div>

          <div class="last-content">
            <p>{{ $t(exportTypeTranslationKey) }}</p>
            <p>{{ $t('modal.updatedOn') }} {{ formatDate(lastExport.updated_at) }}</p>
            <p>{{ $t('comments', lastExport.commentaries_number, { count: lastExport.commentaries_number }) }}</p>
          </div>
        </div>

        <div class="cta-container">
          <button class="study-button main" @click="() => resumeExport(lastExport.public_id)">{{ $t('resume') }}</button>
          <button class="study-button" @click="() => exportStudy()">{{ $t('modal.newExport') }}</button>
        </div>
      </div>
    </div>

    <div v-if="currentExportList && exportList.length > 0" class="previous-export-box">
      <h3>{{ $t('modal.doneExports') }}</h3>

      <div class="previous-container">
        <div v-for="(entry, idx) in exportList" :key="idx" class="previous-entry">
          <div class="icon-container">
            <i class="icon-pdffile" />
          </div>

          <div class="previous-content">
            <span class="title">{{ $t('name') }}: {{ entry.name }}</span>
            <span class="details">{{ $t('modal.updatedOn') }} {{ formatDate(entry.updated_at) }} {{ $t('by') }} {{ entry.exported_by }}</span>
          </div>

          <div class="icon-container" @click="downloadExport(entry.public_id)">
            <i class="icon-download" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Routes } from '@/router/Export';
import ExportType from '@/pure/ExportType';

export default {
  props: ['params'],

  computed: {
    ...mapGetters(['currentExportList']),

    lastExport() {
      return this.currentExportList.last;
    },

    exportList() {
      return this.currentExportList.previous.filter(e => e.status === 7);
    },

    exportTypeTranslationKey() {
      switch (this.lastExport.type) {
        case ExportType.Short:
          return 'modal.shortExport';
        case ExportType.Simplified:
          return 'modal.simpleExport';
        case ExportType.Specific:
          return 'modal.specificExport';
        default:
          return '';
      }
    },
  },

  created() {
    const { id } = this.params;
    this.$store.dispatch('getExportList', { id }).then(data => {
      if (!data.last) {
        this.exportStudy();
      }
    });
  },

  beforeUnmount() {
    this.$store.dispatch('clearExportList');
  },

  methods: {
    resumeExport(id) {
      if (this.lastExport.type === ExportType.Short) {
        this.$router.push({ name: 'exportTransportMode', params: { studyId: id } });
        return;
      }

      let current = Math.max(this.lastExport.status - 1, 0);
      if (this.lastExport.type === ExportType.Simplified) {
        current++;
      }
      const name = Routes[Math.min(current, Routes.length - 1)].name;
      this.$router.push({ name, params: { studyId: id } });
    },

    exportStudy() {
      const { id } = this.params;
      this.$store.dispatch('updateCurrentModal', {
        component: 'ExportStudy',
        params: {
          id,
        },
      });
    },

    downloadExport(exportId) {
      this.$store.dispatch('getExportFile', { id: exportId }).then(e => {
        this.downloadURI(e.url, e.filename);
      });
    },

    downloadExportExcel(exportId) {
      this.$store.dispatch('getExportExcel', { id: exportId }).then(e => {
        this.downloadFile(e);
      });
    },

    downloadFile(blob, name) {
      const BlobUrl = window.URL.createObjectURL(blob.blob);

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(blob.blob, name || blob.name);
      } else {
        const link = document.createElement('a');
        link.download = decodeURI(blob.name);
        link.href = BlobUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    downloadURI(uri, name) {
      const link = document.createElement('a');
      link.download = name;
      link.href = uri;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    formatDate(rawDate) {
      const cd = new Date(rawDate * 1000);
      return `${cd.getDate()}.${cd.getMonth() + 1}.${cd.getFullYear()}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.view-gabarit {
  width: 500px;
  max-width: 100%;
  box-sizing: border-box;
}
h3 {
  font-size: 16px;
  font-weight: 600;
  color: #3c3c3b;
}

.export-content {
  display: flex;

  .cta-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
  }
}

.previous-export-box {
  background-color: #f8f7f7;
  margin-left: -30px;
  margin-right: -30px;
  margin-bottom: -18px;
  padding-left: 52px;
  padding-right: 52px;
  overflow: auto;
  max-height: 50vh;
  padding-bottom: 30px;
  box-sizing: border-box;
  h3 {
    padding-top: 35px;
    padding-bottom: 20px;
  }
  .previous-entry {
    display: flex;
    align-items: center;
    color: #3c3c3b;
    padding-top: 12px;
    padding-bottom: 12px;
    box-sizing: border-box;
    border-top: 1px solid #dddddd;
    .icon-container {
      width: 32px;
      height: 32px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    i {
      font-size: 2.5em;
    }
    .previous-content {
      flex: 1;
      padding-left: 12px;
      box-sizing: border-box;
      span {
        display: block;
        font-size: 14px;
        &.title {
          font-weight: 600;
        }
        &.details {
          opacity: 0.7;
        }
      }
    }
  }
}
.last-box {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  flex: 1;
  .icon-container {
    width: 88px;
    height: 88px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  i {
    color: #dbd6c7;
    font-size: 7em;
  }
  p {
    font-size: 14px;
  }
  .last-content {
    color: #3c3c3b;
    box-sizing: border-box;
    padding-left: 32px;
    padding-right: 40px;
  }
}
</style>
